import React, { useState, useRef, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useContextApi } from "../../../context/ContextApi";
import { PlusIcon, XIcon } from "../../../constants/Icons";

const CashInModal = ({ isActive, onClose, onCashIn, isBtnLoading }) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const amountRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const cashInBtnRef = useRef(null);
  const { useEscapeKey } = useContextApi();
  const handleClose = () => {
    setAmount("");
    setTitle("");
    setDescription("");
    setErrorMessage("");
    onClose();
  };
  useEscapeKey(handleClose);
  const handleCashIn = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onCashIn(amount, title, description);
  };

  const validateForm = () => {
    if (!amount) {
      setErrorMessage("Amount cannot be empty.");
      // If you have a ref for the amount input, you'd focus here:
      amountRef.current?.focus();
      setTitleError("");
      return false;
    }

    const parsedValue = parseFloat(amount);
    if (isNaN(parsedValue) || parsedValue <= 0) {
      setErrorMessage("Enter a valid numeric amount greater than 0.");
      amountRef.current?.focus();
      setTitleError("");
      return false;
    }

    const decimalPart = amount.split(".")[1];
    if (decimalPart && decimalPart.length !== 2) {
      setErrorMessage(
        "Amount must have exactly two decimal places (e.g., 22.22).",
      );
      amountRef.current?.focus();
      setTitleError("");
      return false;
    }
    setErrorMessage("");

    const trimmedTitle = title.trim();
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 100;

    if (!trimmedTitle) {
      setTitleError("Title is required.");
      titleRef.current?.focus();
      return false;
    }

    if (trimmedTitle.length < MIN_LENGTH || trimmedTitle.length > MAX_LENGTH) {
      setTitleError(
        `Title name must be between ${MIN_LENGTH}-${MAX_LENGTH} characters.`,
      );
      titleRef.current?.focus();
      return false;
    }
    setTitleError("");

    return true;
  };
  const handleInputChange = (value) => {
    let inputValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    const [integerPart, decimalPart] = inputValue.split(".");

    if (decimalPart && decimalPart.length > 2) {
      inputValue = `${integerPart}.${decimalPart.slice(0, 2)}`;
    }

    if (inputValue.match(/^0\d/)) {
      inputValue = inputValue.replace(/^0+/, "") || "0";
    }

    const parsedValue = parseFloat(inputValue) || 0;
    if (!inputValue || parsedValue <= 0) {
      setErrorMessage("Enter a valid amount greater than 0.");
      setAmount(inputValue || "");
    } else {
      setErrorMessage("");
      setAmount(inputValue);
    }
  };

  const handleEnterFocus = (e, nextRef = null, isLast = false) => {
    if (e.key !== "Enter") return;

    e.preventDefault?.();

    const value = amount.trim();
    if (!value || parseFloat(value) <= 0) {
      setErrorMessage("Enter a valid amount greater than 0.");
      return;
    }

    if (!title.trim()) {
      setTitleError("Title is required");
      titleRef.current?.focus();
      return;
    } else if (title.trim().length < 1 || title.trim().length > 100) {
      setTitleError(`Title name must be between ${2}-${100} characters.`);
      titleRef.current?.focus();
      return;
    } else {
      setTitleError(""); // Clear the error when valid
    }

    if (isLast) {
      handleCashIn(e);
    } else if (nextRef?.current?.focus) {
      nextRef.current.focus();
    }
  };

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      amountRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Cash In</h6>
              <button
                type="button"
                onClick={handleClose}
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="card w-100 radius-10 mb-0 shadow-none">
                <div className="card-body p-0">
                  <form className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        Amount <small className="text-danger">*</small>
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        ref={amountRef}
                        value={amount}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={(e) => handleEnterFocus(e, titleRef, false)}
                      />
                      {errorMessage && (
                        <small className="text-danger">{errorMessage}</small>
                      )}
                    </div>
                    <div className="col-md-12">
                      <label htmlFor="title" className="form-label">
                        Title <small className="text-danger">*</small>
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        ref={titleRef}
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          if (e.target.value.trim() === "") {
                            setTitleError("Title is required.");
                          } else if (
                            title.trim().length < 1 ||
                            title.trim().length > 100
                          ) {
                            setTitleError(
                              `Title name must be between ${2}-${100} characters.`,
                            );
                            titleRef.current?.focus();
                            return;
                          } else {
                            setTitleError("");
                          }
                        }}
                        onKeyDown={(e) =>
                          handleEnterFocus(e, descriptionRef, false)
                        }
                      ></input>
                      {titleError && (
                        <small className="text-danger">{titleError}</small>
                      )}
                    </div>
                    <div className="col-md-12">
                      <label htmlFor="description" className="form-label">
                        Description
                      </label>
                      <textarea
                        type="text"
                        autoComplete="off"
                        maxLength="5000"
                        className="form-control"
                        ref={descriptionRef}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyDown={(e) =>
                          handleEnterFocus(e, cashInBtnRef, true)
                        }
                      ></textarea>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-footer d-block">
              <button
                onClick={handleClose}
                type="button"
                data-bs-dismiss="modal"
                className="btn btn-outline-secondary text-left"
              >
                <XIcon/> Close
              </button>

              {isBtnLoading ? (
                <button className="btn btn-success pb-0 text-right" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-success text-right"
                  onClick={handleCashIn}
                  ref={cashInBtnRef}
                >
                  <PlusIcon/> Cash In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashInModal;
